import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {
  CheckoutStationOverviewItem,
  CheckoutStationOverviewListQuery,
  CheckoutStationOverviewListResponse,
} from '../../../interfaces/CheckoutStationOverview';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {ShiftSelect} from '../../selectors/ShiftSelect';

interface Props {
  value?: CheckoutStationOverviewReportGridData;
  onChange?: (value: CheckoutStationOverviewReportGridData) => void;
}

const DEFAULT_SHOWN_FIELDS = ['shift', 'total', 'passed', 'failed'];

export interface CheckoutStationOverviewReportGridData {
  refreshInterval?: number | null;
  shownFields: string[];
  params: {
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
    date_start?: string;
    date_end?: string;
    shift_id?: number | null;
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
}

export const getCheckoutStationOverviewReportGridData =
  (): CheckoutStationOverviewReportGridData => ({
    shownFields: DEFAULT_SHOWN_FIELDS,
    params: {},
    columnWidths: {},
    columnOrder: undefined,
  });

const CheckoutStationOverviewReportGrid = ({value, onChange}: Props) => {
  const config = useMemo(
    () => value ?? getCheckoutStationOverviewReportGridData(),
    [value]
  );

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<CheckoutStationOverviewListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<CheckoutStationOverviewListQuery>(
    () => ({
      date_start: config.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
      date_end: config.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
      page: config.params?.page ?? 0,
      limit: config.params?.limit ?? 25,
      order: config.params?.order ?? 'shift',
      dir: config.params?.dir ?? 'DESC',
      shift_id: config.params?.shift_id ?? null,
    }),
    [config.params]
  );

  const fetchData = useCallback(
    async (params: CheckoutStationOverviewListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const resp = await API.get<CheckoutStationOverviewListResponse>(
          `${apiBaseUrl}/report/checkout-overview-grid`,
          {params}
        );
        setFetchedData(resp.data);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, "STRA_1c0512a7");
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  /****************/
  /* auto refresh */
  /****************/
  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);

  useRefreshInterval(callFetchData, config.refreshInterval);

  /*************/
  /* data grid */
  /*************/
  const shownFields = useMemo(
    () => config.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [config.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<CheckoutStationOverviewItem>[] = [
    {
      field: 'shift',
      headerName: 'Shift Report Time',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'total',
      headerName: 'Total Tested',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'passed',
      headerName: 'Passed',
      width: 80,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'failed',
      headerName: 'Failed',
      width: 80,
      resizeable: true,
      sortable: true,
    },
  ];

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onChange?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      onChange?.(
        update(config, {
          shownFields: {$set: fields},
        })
      );
    },
    [onChange, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onChange?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
        })
      );
    },
    [onChange, config]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="hidden"
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        py={2}
      >
        <Box flexGrow={2} display="flex">
          <Box minWidth={400} mr={1}>
            <DateRangeSelect
              value={[
                dayjs(params.date_start).toDate(),
                dayjs(params.date_end).toDate(),
              ]}
              size="small"
              onChange={(v) => {
                onChange?.(
                  update(config, {
                    params: {
                      date_start: {
                        $set: v?.[0]
                          ? dayjs(v?.[0]).format('YYYY-MM-DD')
                          : undefined,
                      },
                      date_end: {
                        $set: v?.[1]
                          ? dayjs(v?.[1]).format('YYYY-MM-DD')
                          : undefined,
                      },
                    },
                  })
                );
              }}
            />
          </Box>

          <ShiftSelect
            value={config.params?.shift_id}
            size="small"
            nullLabel="All Shifts"
            fullWidth
            sx={{width: 200}}
            onChange={(v) => {
              onChange?.(
                update(config, {
                  params: {
                    shift_id: {
                      $set: v,
                    },
                  },
                })
              );
            }}
          />
        </Box>

        <Box display="flex">
          <ButtonGroup>
            <Button size="small" onClick={() => params && fetchData(params)}>
              <RefreshIcon />
            </Button>

            <AutoRefreshSelect
              value={config.refreshInterval ?? null}
              onChange={(v) => {
                onChange?.(
                  update(config, {
                    refreshInterval: {
                      $set: v,
                    },
                  })
                );
              }}
            />

            <Button
              size="small"
              onClick={() => dataGridRef.current?.printTable()}
            >
              <PrintIcon />
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <Backdrop
        open={fetchedInProgress}
        sx={{position: 'absolute', zIndex: 1199}}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        shownFields={shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={config.columnWidths}
        columnOrder={config.columnOrder}
        pagination
        paginationMode="server"
        size="small"
        sortBy={
          params?.order
            ? {
                field: params?.order,
                dir: params?.dir === 'DESC' ? 'desc' : 'asc',
              }
            : null
        }
        sortingMode="server"
        page={params?.page}
        pageSize={params?.limit}
        rowCount={fetchedData?.count}
        sxFooter={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
        }}
        onPageChange={(v) => {
          onChange?.(
            update(config, {
              params: {
                page: {
                  $set: v,
                },
              },
            })
          );
        }}
        onPageSizeChange={(v) => {
          onChange?.(
            update(config, {
              params: {
                page: {
                  $set: 0,
                },
                limit: {
                  $set: v,
                },
              },
            })
          );
        }}
        onSort={(v) => {
          onChange?.(
            update(config, {
              params: {
                order: {
                  $set: v?.field,
                },
                dir: {
                  $set: v?.dir === 'desc' ? 'DESC' : 'ASC',
                },
              },
            })
          );
        }}
        onShownFieldsChange={handleShownFieldsChange}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
      />
    </Box>
  );
};

export default CheckoutStationOverviewReportGrid;
